import {
  EvidenceHomepageVerbs,
  type EvidenceVerb,
  type EvidenceVerbSuggestionItem,
} from '@heidi/evidence';
import { EXPLORE_VERB_LABEL } from '@/helpers/explore-verb-label';
import { getRegion } from '@/helpers/getRegion';
import { isTreatWordingRestricted } from '@/helpers/is-treat-wording-restricted';
import { getCurrentUser } from '@heidi/workspace/data';
import { Country } from '@heidi/utils/countries';
import { type TranslateFunction, useTranslations } from '@heidi/runtime/i18n';
import { Region } from '@heidi/schema/ml-scribe';
import type { LucideIcon } from 'lucide-react';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useIntl, type IntlShape } from 'react-intl';

import type {
  EvidenceStarterTaxonomy,
  EvidenceVerbCardId,
} from '../evidence-verb-suggestions.types';
import {
  WORK_HOME_RECUT_VERBS,
  type WorkHomeVerb,
  type WorkHomeVerbItem,
} from '../work-home-verb-starters';

export interface ResolvedVerbItem {
  id: string;
  patternLabel: string;
  hoverPreview: string;
  starter: string;
  /** Optional trigger character appended to starter to auto-open an autocomplete menu */
  triggerSuffix?: '@' | '#';
}

export interface ResolvedVerb {
  id: EvidenceVerbCardId;
  label: string;
  icon: LucideIcon;
  items: ResolvedVerbItem[];
}

const isInRegion = (item: EvidenceVerbSuggestionItem, region: Region): boolean => {
  if (region === Region.US) return item.locale === 'US' || item.locale === 'global';
  if (region === Region.AU) return item.locale === 'AU' || item.locale === 'global';
  return true;
};

const itemCopy = (
  item: EvidenceVerbSuggestionItem,
  useRetrievalCopy: boolean
): Pick<EvidenceVerbSuggestionItem, 'patternLabelKey' | 'hoverPreviewKey' | 'starterKey'> =>
  useRetrievalCopy && item.nonUsVariant !== undefined ? item.nonUsVariant : item;

const resolveItem = (
  item: EvidenceVerbSuggestionItem,
  t: TranslateFunction,
  useRetrievalCopy: boolean
): ResolvedVerbItem => {
  const copy = itemCopy(item, useRetrievalCopy);
  return {
    id: item.id,
    patternLabel: t(copy.patternLabelKey),
    hoverPreview: t(copy.hoverPreviewKey),
    starter: t(copy.starterKey),
    triggerSuffix: item.triggerSuffix,
  };
};

const homepageItemsById = new Map(
  EvidenceHomepageVerbs.flatMap((verb) => verb.items.map((item) => [item.id, item] as const))
);

/**
 * A recut row resolves override-first, then through the catalogue row its
 * `reuseFrom` names — through that row's retrieval (`nonUsVariant`) copy where it
 * has one, in every market, because this set is one global copy set. A pointer
 * the catalogue does not carry, a source outside the region, or a row left
 * without all three strings is dropped rather than rendered as a blank chip.
 */
export const resolveRecutItem = (
  item: WorkHomeVerbItem,
  t: TranslateFunction,
  intl: IntlShape,
  region: Region
): ResolvedVerbItem | null => {
  const source = item.reuseFrom ? homepageItemsById.get(item.reuseFrom) : undefined;
  if (item.reuseFrom && (!source || !isInRegion(source, region))) return null;
  const sourceKeys = source?.nonUsVariant ?? source;

  const patternLabel = item.patternLabel
    ? intl.formatMessage(item.patternLabel)
    : sourceKeys && t(sourceKeys.patternLabelKey);
  const hoverPreview = item.hoverPreview
    ? intl.formatMessage(item.hoverPreview)
    : sourceKeys && t(sourceKeys.hoverPreviewKey);
  const starter = item.starter
    ? intl.formatMessage(item.starter)
    : sourceKeys && t(sourceKeys.starterKey);

  if (!patternLabel || !hoverPreview || !starter) return null;

  return {
    id: item.id,
    patternLabel,
    hoverPreview,
    starter,
    triggerSuffix: source?.triggerSuffix,
  };
};

const resolveRecutVerb = (
  verb: WorkHomeVerb,
  t: TranslateFunction,
  intl: IntlShape,
  region: Region
): ResolvedVerb => ({
  id: verb.id,
  label:
    verb.label.kind === 'reuse' ? t(verb.label.key) : intl.formatMessage(verb.label.message),
  icon: verb.icon,
  items: verb.items
    .map((item) => resolveRecutItem(item, t, intl, region))
    .filter((item): item is ResolvedVerbItem => item !== null),
});

/**
 * Resolves verb definitions into translated, region-filtered data
 * ready for rendering.
 *
 * On the shipped catalogue, EU/EEA and UK clinicians (and anyone whose country
 * is unknown) get "Explore" on the treat chip and the retrieval-oriented item
 * copy. The verb id stays `treat` so analytics still join. Japan hides the
 * verb. The Work homepage recut is a separate global copy set and does not
 * read this gate.
 */
export const useEvidenceHomepageVerbs = (
  taxonomy: EvidenceStarterTaxonomy = 'shipped'
): ResolvedVerb[] => {
  const { t } = useTranslations();
  const intl = useIntl();
  const region = getRegion();
  const router = useRouter();
  const isSessionless = !!router.pathname && router.pathname.startsWith('/chat');
  const country = getCurrentUser()?.country;
  const isJapan = country === Country.JAPAN;
  const restrictTreatWording = isTreatWordingRestricted(country);

  return useMemo(() => {
    const passesFilters = (item: EvidenceVerbSuggestionItem) => {
      const preview = t(itemCopy(item, restrictTreatWording).hoverPreviewKey);
      if (isSessionless && preview.includes('[patient details]')) {
        return false;
      }
      return isInRegion(item, region);
    };

    const resolveCatalogueVerb = (verb: EvidenceVerb): ResolvedVerb => ({
      id: verb.id,
      label:
        verb.id === 'treat' && restrictTreatWording
          ? intl.formatMessage(EXPLORE_VERB_LABEL)
          : t(verb.labelKey),
      icon: verb.icon,
      items: verb.items
        .filter(passesFilters)
        .map((item) => resolveItem(item, t, restrictTreatWording)),
    });

    if (taxonomy === 'directionC') {
      // Direction C's five cards. Japan still hides the card whose id is `treat`.
      return WORK_HOME_RECUT_VERBS.filter((verb) => !(isJapan && verb.id === 'treat')).map(
        (verb) => resolveRecutVerb(verb, t, intl, region)
      );
    }

    const verbsToRender = isJapan
      ? EvidenceHomepageVerbs.filter((verb) => verb.id !== 'treat')
      : EvidenceHomepageVerbs;

    return verbsToRender.map(resolveCatalogueVerb);
  }, [
    t,
    intl,
    region,
    isSessionless,
    isJapan,
    restrictTreatWording,
    taxonomy,
  ]);
};
